import { compareVersion, isValidDomain } from '../check.js';
import { urlParse } from '../url.js';
import { Style } from '../style.js';
export const isClient = typeof window !== 'undefined';
const CURRENT_BASE_URL = isClient ? `${location.protocol}//${location.host}` : '';
/**
 * 基础的环境类，封装浏览器通用的环境判断
 */
export class BaseEnv {
    constructor(options = {}) {
        this.ua = '';
        this.isIOS = false; // 是否是 iOS 系统
        this.isAllScreen = false; // 是否是全面屏
        this.isIPad = false; // 是否是 iPad，iPad 也属于 iOS 里，用于一些特殊场景的判断
        this.isAndroid = false; // 是否是 android 系统
        this.isAndroidPad = false; // 是否是 android 平板
        this.isWx = false; // 是否是微信
        this.isMiniProgram = false; // 是否是小程序 webView
        this.isQq = false; // 是否是 qq webView
        this.isTrident = false; // 是否是 trident 内核（ie 浏览器）
        this.isEdge = false; // 是否是 edge 浏览器
        this.isFirefox = false; // 是否是 firefox 浏览器
        this.isSafari = false; // 是否是 safari 浏览器
        this.isDesktop = false; // 是否是桌面浏览器
        this.isWindows = false; // 是否是 windows 操作系统
        this.isMacOS = false; // 是否是 mac 操作系统
        this.iOSVersion = '10.0.0'; // iOS 版本号
        this.androidVersion = '5.0.0'; // android 版本号
        this.edgeVersion = ''; // edge 版本号
        this.firefoxVersion = ''; // firefox 版本号
        this.safariVersion = ''; // safari 版本号
        this.screenWidth = 375;
        this.screenHeight = 667;
        this.devicePixelRatio = 2;
        this.statusBarHeight = 20;
        this.navigationBarHeight = 44;
        this.isProd = false;
        this.isWKWebView = false; // 是否是 WKWebView，现在 app 应该全部都是，2020年后不是 WKWebView 的不能上架 AppStore
        this.isPageShow = false;
        this.pageShowed = false;
        this.envVarPrefix = '--app--'; // css env vars 前缀
        this._mainDomain = '';
        this._isClient = isClient;
        this._currentBaseUrl = CURRENT_BASE_URL;
        this._isDeveloperTools = false;
        this._isWebDev = false;
        if ('envVarPrefix' in options) {
            this.envVarPrefix = options.envVarPrefix;
        }
        if (this._isClient) {
            const customWindow = window;
            this._isDeveloperTools = !!(customWindow.chrome || customWindow.safari);
            this.refreshFromClient();
            this.refreshStatusBarHeight();
            setTimeout(() => {
                this.preventIOSDbclickPageMove();
            }, 0);
            if (document.readyState === 'complete') {
                this.isPageShow = true;
                this.pageShowed = true;
            }
            else {
                window.addEventListener('pageshow', () => {
                    this.isPageShow = true;
                    this.pageShowed = true;
                });
            }
            window.addEventListener('beforeunload', () => {
                this.isPageShow = false;
            });
            window.addEventListener('pagehide', () => {
                this.isPageShow = false;
            });
        }
    }
    /**
     * 如果在浏览器，根据环境刷新
     */
    refreshFromClient() {
        if (this.isClient) {
            this.refreshWithUaAndHref(window.navigator.userAgent, window.location.href);
            this.screenWidth = window.screen.width;
            this.screenHeight = window.screen.height;
            this.devicePixelRatio = window.devicePixelRatio || 1;
            this.isAllScreen =
                this.isIOS &&
                    (this.screenHeight == 812 || this.screenHeight == 896 || this.screenHeight == 844 || this.screenHeight == 926);
            if (this.isAllScreen) {
                this.statusBarHeight = 44; // 这只是一个预计的高度，实际不同机型的状态栏高度不同
            }
        }
    }
    /**
     * 根据ua和链接刷新环境，如果是服务端渲染，需手动调用
     * @param userAgent
     * @param href
     */
    refreshWithUaAndHref(userAgent, href) {
        this.refreshWithUa(userAgent);
        this.refreshWithHref(href);
    }
    /**
     * 根据ua刷新环境，如果是服务端渲染，需手动调用
     * @param {*} userAgent
     */
    refreshWithUa(userAgent) {
        const ua = userAgent.toLowerCase();
        this.ua = ua;
        this.isWx = /micromessenger/.test(ua);
        if (ua.indexOf('webdev') > -1) {
            this._isWebDev = true;
        }
        this.isQq = ua.match(/\sqq/i) !== null;
        this.isAndroid = ua.indexOf('android') > -1 || ua.indexOf('adr') > -1;
        this.isAndroidPad = this.isAndroid && ua.indexOf('mobile') === -1;
        this.isIPad = /ipad/.test(ua);
        this.isIOS = /iphone|ipad|ipod|iOS/.test(ua);
        this.isMiniProgram = ua.includes('miniprogram');
        this.isTrident = ua.includes('trident/');
        this.isMacOS = /mac\sos\sx\s([\d_]+)/.test(ua);
        this.isWindows = /windows\snt/.test(ua);
        const edgeRes = ua.match(/edge\/([\d.]+)/);
        if (edgeRes) {
            this.isEdge = true;
            this.edgeVersion = edgeRes[1];
        }
        else {
            const firefoxRes = ua.match(/firefox\/([\d.]+)/);
            if (firefoxRes) {
                this.isFirefox = true;
                this.firefoxVersion = firefoxRes[1];
            }
        }
        if ((this.isIOS || this.isMacOS) && ua.includes('safari/')) {
            const safariRes = ua.match(/version\/([\d.]+)/);
            if (safariRes) {
                this.isSafari = true;
                this.safariVersion = safariRes[1];
            }
        }
        if (this.isIOS) {
            const regStrSaf = /os [\d._]*/gi;
            const verinfo = ua.match(regStrSaf);
            this.iOSVersion = (verinfo + '').replace(/[^0-9|_.]/gi, '').replace(/_/gi, '.') + '';
            // 非 app 环境只要 iOS 系统版本大于 8.0.0 都当使用了 wkwebview
            this.isWKWebView = compareVersion(this.iOSVersion, '8.0.0') >= 0;
        }
        else if (this.isAndroid) {
            const regStrSaf = /(android|adr) [\d._]*/gi;
            const verinfo = ua.match(regStrSaf);
            this.androidVersion = (verinfo + '').replace(/[^0-9|_.]/gi, '').replace(/_/gi, '.') + '';
            this.navigationBarHeight = 49;
            this.statusBarHeight = 30;
        }
        else {
            // 非 iOS 和 android 都当做桌面浏览器
            this.isDesktop = true;
        }
    }
    /**
     * 根据链接刷新应用环境，如果是服务端渲染，需手动调用
     * @param {*} href
     */
    refreshWithHref(href) {
        if (href) {
            const { host, protocol, hostname } = urlParse(href);
            if (!this._currentBaseUrl || !this.isClient) {
                this._currentBaseUrl = `${protocol}//${host}`;
            }
            let mainDomain = '';
            if (!isValidDomain(hostname)) {
                mainDomain = hostname;
            }
            else {
                const regex = /([^]*).*/;
                const match = hostname.match(regex);
                if (typeof match !== 'undefined' && match !== null) {
                    mainDomain = match[1];
                }
                if (typeof hostname !== 'undefined' && hostname !== null) {
                    const strAry = hostname.split('.');
                    if (strAry.length > 1) {
                        mainDomain = strAry[strAry.length - 2] + '.' + strAry[strAry.length - 1];
                    }
                }
            }
            this._mainDomain = mainDomain;
        }
    }
    /**
     * 通过浏览器获取状态栏高度，原理如下：
     * 1. 页面初始化的时候注入一段 style，将 env(safe-area-inset-top) 等一些 css环境变量赋值给 css 变量（var)
     * 2. 通过 dom 接口获取 css 属性来得到 env(safe-area-inset-top) 等的值
     */
    refreshStatusBarHeight() {
        if (this.isClient) {
            this.appendEnvVars();
            // 无法立即获取到值
            setTimeout(() => {
                this._refreshStatusBarHeight();
            }, 0);
        }
    }
    appendEnvVars() {
        const style = Style.getInstance();
        if (!style.checkVarExists(`${this.envVarPrefix}sait`)) {
            style.appendStyle(`
:root {
  ${this.envVarPrefix}sait: env(safe-area-inset-top);
  ${this.envVarPrefix}sair: env(safe-area-inset-right);
  ${this.envVarPrefix}saib: env(safe-area-inset-bottom);
  ${this.envVarPrefix}sail: env(safe-area-inset-left);
}`);
        }
    }
    _refreshStatusBarHeight() {
        const val = getComputedStyle(document.documentElement)
            .getPropertyValue(`${this.envVarPrefix}sait`)
            .replace(/\s/gi, '')
            .replace('px', '');
        if (val) {
            const number = parseInt(val);
            if (number) {
                this.statusBarHeight = number;
            }
        }
    }
    /**
     * @description 解决iOS场景双击页面滑动问题,对页面元素本身设置prevent属性以下代码将不会执行
     */
    preventIOSDbclickPageMove() {
        let iLastTouch = 0; //缓存上一次tap的时间
        if (this.isIOS && this._isClient) {
            document.body.addEventListener('touchend', (event) => {
                const iNow = new Date().getTime();
                iLastTouch = iLastTouch || iNow + 1; // 第一次时将iLastTouch设为当前时间+1
                let delta = iNow - iLastTouch;
                if (delta < 500 && delta > 0) {
                    event.preventDefault();
                    return false;
                }
                iLastTouch = iNow;
            }, false);
        }
    }
    set currentBaseUrl(val) {
        this._currentBaseUrl = val;
    }
    get currentBaseUrl() {
        return this._currentBaseUrl;
    }
    /**
     * 是否开启页面转场动画，低版本手机上直接不开启，卡顿
     */
    get enablePageTransition() {
        return ((!this.isIOS && !this.isAndroid) ||
            (this.isIOS && compareVersion(this.iOSVersion, '10.0.0') >= 0) ||
            (this.isAndroid && compareVersion(this.androidVersion, '6.0.0') >= 0));
    }
    /**
     * 主域名
     */
    get mainDomain() {
        return this._mainDomain;
    }
    /**
     * 是否是浏览器
     */
    get isClient() {
        return this._isClient;
    }
    /**
     * 是否是微信小程序
     */
    get isWxMini() {
        return this.isWx && this.isMiniProgram;
    }
    /**
     * 客户端是否支持webp，根据 ua 判断主流浏览器的版本号
     * @link https://caniuse.com/?search=webp
     */
    get canUseWebp() {
        return ((this.isIOS && compareVersion(this.iOSVersion, '14.0.0') >= 0) ||
            (this.isAndroid && compareVersion(this.androidVersion, '4.2.0') >= 0) ||
            (this.isSafari && compareVersion(this.safariVersion, '16.0.0') >= 0) ||
            (this.isEdge && compareVersion(this.edgeVersion, '18.0.0.0') >= 0) ||
            (this.isFirefox && compareVersion(this.firefoxVersion, '65') >= 0) ||
            !this.isTrident);
    }
    /**
     * 是否需要fastClick，在android 4.4 以及使用 wkwebview 内核的 iOS 9.3以上版本，通过在 meta->viewport 设置 user-scalable=no 可以不用引入fastClick
     */
    get needFastClick() {
        return !(this.isWx ||
            (!this.isIOS && !this.isAndroid) ||
            (this.isIOS && this.isWKWebView && compareVersion(this.iOSVersion, '9.3') >= 0) ||
            (this.isAndroid && compareVersion(this.androidVersion, '4.4') >= 0));
    }
    /**
     * 是否是微信环境，包含微信内网页和小程序webview
     */
    get isEnvWx() {
        return this.isWx || this.isWxMini;
    }
    /**
     * 是否是开发者工具，主要用于判断是否是ua模拟客户端，避免某些流程走不下去
     */
    get isDeveloperTools() {
        return this._isDeveloperTools;
    }
    /**
     * 是否是移动端环境
     */
    get isEnvMobile() {
        return ((this.isIOS && !this.isIPad) ||
            (this.isAndroid && !this.isAndroidPad) ||
            (this.isClient && window.innerWidth < 501));
    }
    /**
     * 是否是平板环境
     */
    get isEnvPad() {
        return this.isIPad || this.isAndroidPad;
    }
}
